import type { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import {
  DEFAULT_ERROR_MESSAGE,
  RetrieveSavedBasketErrorCodes,
} from '@constants/apiErrors';
import { ValidationErrorMessages } from '@constants/forms';
import { paths } from '@constants/paths';
import { BASKET_RETRIEVAL_TOKEN } from '@constants/queryParams';
import {
  OccupancyType,
  RetrieveSavedBasketQuery,
} from '@generated/gql/graphql';
import { useAttribution } from '@hooks/useAttribution';
import { useSnackbarErrors } from '@hooks/useSnackbarErrors';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useLocalSessionAddressContext } from '@providers/basket/LocalSessionAddressProvider/Context';
import {
  signupApplicationEntity,
  useTrackSignupOnequoteRestoreContinued,
} from '@tracking/trackingEvents';
import { parseSessionContext } from '@tracking/utils/parseAttributionContext';
import { containsErrorCode } from '@utils/apiErrors';

import { parseAddressFormInput } from '../parseAddressFormInput';
import { getRetrievedBasket } from '../query';
import { useLocalSessionAddress } from './useLocalSessionAddress';

const INVALID_TOKEN_ERROR = 'Invalid basket retrieval token';
const MISSING_RETRIEVAL_TOKEN_ERROR =
  'Basket retrieval token is missing in the context';

const useVerifyModal = () => {
  const dialog = useDialog(DialogID.RESTORE_VERIFY);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { addAssistantDetails } = useAttribution();

  const { handleBasketCreated: restoreBasketContext } = useBasketContext();
  const { updateAddressPageStorage } = useLocalSessionAddressContext();

  const [retrieveBasket, { loading: restoreLoading }] =
    useLazyQuery(getRetrievedBasket);
  const { getAddress, loading: addressLoading } = useLocalSessionAddress();

  const [postcodeError, setPostcodeError] = useState<Nullable<string>>(null);
  const [retrievalToken, setRetrievalToken] = useState<Nullable<string>>(null);
  const [submitErrors, setSubmitErrors] = useState<ErrorLike[]>([]);
  useSnackbarErrors(submitErrors);
  const trackRestore = useTrackSignupOnequoteRestoreContinued();

  const token = searchParams.get(BASKET_RETRIEVAL_TOKEN);

  // save the `retrievalToken` search parameter in local state, so it can persist
  // across route navigations. Since Address is no longer the landing page, we
  // can not rely on `searchParams` by itself
  useEffect(() => {
    if (token) {
      setRetrievalToken(token);
      dialog.setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setSearchParams({});
    setRetrievalToken(null);
    dialog.setOpen(false);
  };

  const handleError = (errorMessage: string) => {
    handleClose();
    setSubmitErrors([new Error(errorMessage)]);
  };

  const getSessionAddress = async (basketId: string) => {
    try {
      const result = await getAddress({ variables: { basketId } });
      if (result.data) {
        updateAddressPageStorage(parseAddressFormInput(result.data));
      }
    } catch (error) {
      setSubmitErrors([error as ErrorLike]);
    }
  };

  const handleRetrievalFailed = (error: ErrorLike) => {
    if (
      containsErrorCode(RetrieveSavedBasketErrorCodes.invalidPostcode, error)
    ) {
      setPostcodeError(ValidationErrorMessages.INVALID_POSTCODE);
      trackRestore({ postcode_check_success: false });
      return;
    }

    if (containsErrorCode(RetrieveSavedBasketErrorCodes.invalidToken, error)) {
      setSubmitErrors([new Error(INVALID_TOKEN_ERROR)]);
    } else {
      setSubmitErrors([error]);
    }

    handleClose();
  };

  const handleRetrievalSucceeded = (data: RetrieveSavedBasketQuery) => {
    const basket = data?.retrieveSavedBasket?.basket;
    const basketId = basket?.id;

    if (!basketId) {
      handleError(DEFAULT_ERROR_MESSAGE);
      return;
    }

    addAssistantDetails(basketId, false);

    const applicationContext = {
      created_at: basket.details.createdAt,
      basket_id: basketId,
      supply_postcode: basket.details.supplyAddress.postcode,
      is_home_owner: basket.details.occupancyType === OccupancyType.Homeowner,
      ...parseSessionContext(null, null),
    };

    restoreBasketContext(basketId);
    handleClose();
    navigate(paths.bundle);
    trackRestore(
      { postcode_check_success: true },
      { contexts: [signupApplicationEntity(applicationContext)] }
    );
    return getSessionAddress(basketId);
  };

  const handleBasketRetrieval = async (postcode: string) => {
    if (!retrievalToken) {
      handleError(MISSING_RETRIEVAL_TOKEN_ERROR);
      return;
    }

    try {
      const result = await retrieveBasket({
        variables: { input: { postcode, retrievalToken } },
      });
      if (result.error) {
        handleRetrievalFailed(result.error as unknown as ErrorLike);
      } else if (result.data) {
        handleRetrievalSucceeded(result.data);
      }
    } catch (error) {
      handleRetrievalFailed(error as unknown as ErrorLike);
    }
  };

  return {
    token: retrievalToken,
    isLoading: restoreLoading || addressLoading,
    postcodeError,
    resetPostcodeError: () => {
      setPostcodeError(null);
    },
    handleClose,
    handleBasketRetrieval,
  };
};

export { useVerifyModal, MISSING_RETRIEVAL_TOKEN_ERROR, INVALID_TOKEN_ERROR };
